import React, {
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import BackgroundAnimationController from "./BackgroundAnimationController";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { useSelector, useDispatch } from "react-redux";
import { selectHoldAndWinActive } from "../../../State/selectors";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import {
  attemptFullscreenToggle,
  isPhysicalDesktop,
  isInFullscreen,
} from "../../../utilities";
import store from "../../../State/Store/store";

// Rate limiting for background clicks - prevents performance degradation from rapid clicking
let lastBackgroundClickTime = 0;
const MIN_BACKGROUND_CLICK_GAP = 100; // Same throttling as play buttons (~10 clicks/sec max)

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let backgroundClickThrottleActive = false;
let backgroundClickThrottleTimeout = null;

const BackgroundAnimation = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const backgroundControllerRef = useRef(null);
  const backgroundContainerRef = useRef(null);
  const backgroundSpriteTopLeftRef = useRef(null);
  const backgroundSpriteTopRightRef = useRef(null);
  const backgroundSpriteBottomLeftRef = useRef(null);
  const backgroundSpriteBottomRightRef = useRef(null);
  const { dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation } =
    useDimensions();
  const { backgroundAnimationControllerRef } = useReelRefs();

  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  // Select textures based on Hold and Win active state (4 quadrants)
  const textures = useMemo(() => {
    const prefix = holdAndWinActive ? "feature" : "base";
    return {
      topLeft: PIXI.Assets.get(ASSET_NAME[`${prefix}_top_left`]),
      topRight: PIXI.Assets.get(ASSET_NAME[`${prefix}_top_right`]),
      bottomLeft: PIXI.Assets.get(ASSET_NAME[`${prefix}_bottom_left`]),
      bottomRight: PIXI.Assets.get(ASSET_NAME[`${prefix}_bottom_right`]),
    };
  }, [holdAndWinActive]);

  // Initialize BackgroundAnimationController when assets are ready
  useEffect(() => {
    if (
      backgroundContainerRef.current &&
      backgroundSpriteTopLeftRef.current &&
      backgroundSpriteTopRightRef.current &&
      backgroundSpriteBottomLeftRef.current &&
      backgroundSpriteBottomRightRef.current &&
      !backgroundControllerRef.current
    ) {
      backgroundControllerRef.current = new BackgroundAnimationController(
        backgroundContainerRef.current,
        {
          topLeft: backgroundSpriteTopLeftRef.current,
          topRight: backgroundSpriteTopRightRef.current,
          bottomLeft: backgroundSpriteBottomLeftRef.current,
          bottomRight: backgroundSpriteBottomRightRef.current,
        },
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        device,
        orientation,
      );

      // Store controller in context ref for access from other components
      backgroundAnimationControllerRef.current =
        backgroundControllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose controller methods via ref
  useImperativeHandle(
    ref,
    () => ({
      getController: () => backgroundControllerRef.current,
      playBaseIdle: () => backgroundControllerRef.current?.playBaseIdle(),
      playFeatureIdle: () => backgroundControllerRef.current?.playFeatureIdle(),
      playBaseEventIn: () => backgroundControllerRef.current?.playBaseEventIn(),
      playBaseEventOut: () =>
        backgroundControllerRef.current?.playBaseEventOut(),
    }),
    [],
  );

  // Handle dimension changes for responsive design
  useEffect(() => {
    if (backgroundControllerRef.current) {
      backgroundControllerRef.current.resizeBackground(
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        device,
        orientation,
      );
    }
  }, [dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation]);

  // Update textures when Hold and Win state changes
  useEffect(() => {
    if (
      backgroundSpriteTopLeftRef.current &&
      backgroundSpriteTopRightRef.current &&
      backgroundSpriteBottomLeftRef.current &&
      backgroundSpriteBottomRightRef.current &&
      textures.topLeft &&
      textures.topRight &&
      textures.bottomLeft &&
      textures.bottomRight
    ) {
      backgroundSpriteTopLeftRef.current.texture = textures.topLeft;
      backgroundSpriteTopRightRef.current.texture = textures.topRight;
      backgroundSpriteBottomLeftRef.current.texture = textures.bottomLeft;
      backgroundSpriteBottomRightRef.current.texture = textures.bottomRight;

      // Trigger controller to reposition sprites after texture change
      if (backgroundControllerRef.current) {
        backgroundControllerRef.current.applyResponsiveBackgroundScaling();

        // Also ensure correct animation is playing after texture change
        if (backgroundControllerRef.current.backgroundSpine?.state) {
          if (holdAndWinActive) {
            backgroundControllerRef.current.playFeatureIdle();
          } else {
            backgroundControllerRef.current.playBaseIdle();
          }
        }
      }
    }
  }, [textures, holdAndWinActive]);

  // Play appropriate idle animation based on Hold and Win state
  useEffect(() => {
    if (!backgroundControllerRef.current) {
      return;
    }

    // Only clear track 0 (idle animation), leave track 1 (tension) intact
    if (backgroundControllerRef.current.backgroundSpine?.state) {
      backgroundControllerRef.current.backgroundSpine.state.clearTrack(0);
    }

    if (holdAndWinActive) {
      // Play feature_idle when Hold and Win is active
      backgroundControllerRef.current.playFeatureIdle();
    } else {
      // Play base_idle for base game
      backgroundControllerRef.current.playBaseIdle();
    }
  }, [holdAndWinActive]);

  // Handle background click to toggle fullscreen and close InfoOverlay popup
  const handleBackgroundClick = useCallback(
    (event) => {
      // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
      // Prevents performance degradation from rapid clicking (50+ clicks/sec)
      if (backgroundClickThrottleActive) {
        return; // Instant exit - zero overhead, no logs, no code execution
      }

      // Set throttle flag IMMEDIATELY and schedule single reset
      backgroundClickThrottleActive = true;
      if (backgroundClickThrottleTimeout) {
        clearTimeout(backgroundClickThrottleTimeout);
      }
      backgroundClickThrottleTimeout = setTimeout(() => {
        backgroundClickThrottleActive = false;
        backgroundClickThrottleTimeout = null;
      }, MIN_BACKGROUND_CLICK_GAP);

      // Secondary rate limiting check
      const now = Date.now();
      const timeSinceLastClick = now - lastBackgroundClickTime;
      if (timeSinceLastClick < MIN_BACKGROUND_CLICK_GAP) {
        return;
      }
      lastBackgroundClickTime = now;

      // Stop event propagation to prevent bubbling
      if (event?.stopPropagation) {
        event.stopPropagation();
      }

      // InfoOverlay's background click area handles closing symbol popups
      // No need to dispatch closeSymbolPopup here

      // GUARD: Only attempt fullscreen toggle if conditions might be met
      // Check device type and fullscreen state BEFORE calling the function
      // This prevents unnecessary lazy imports, state reads, and function calls
      const isActualDesktop = isPhysicalDesktop();
      const alreadyInFullscreen = isInFullscreen();
      const fullscreenSupported = store.getState().settings.fullscreenSupported;

      if (!isActualDesktop && !alreadyInFullscreen && fullscreenSupported) {
        attemptFullscreenToggle(dispatch, device);
      }
    },
    [dispatch, device],
  );

  // Memoize sprite dimensions for click detection sprite - prevents recalculation on every render
  const spriteDimensions = useMemo(
    () => ({
      width: dimensions.width,
      height: dimensions.height,
    }),
    [dimensions.width, dimensions.height],
  );

  // Cleanup throttle timeout on unmount
  useEffect(() => {
    return () => {
      if (backgroundClickThrottleTimeout) {
        clearTimeout(backgroundClickThrottleTimeout);
        backgroundClickThrottleTimeout = null;
        backgroundClickThrottleActive = false;
      }
    };
  }, []);

  return (
    <Container ref={backgroundContainerRef} name="background">
      {/* Background quadrants - positioned as puzzle pieces */}
      <Container name="background_quadrants" anchor={0.5}>
        {/* Top-left quadrant */}
        <Sprite
          ref={backgroundSpriteTopLeftRef}
          name="background_top_left"
          texture={textures.topLeft}
          anchor={[1, 1]}
          x={0}
          y={0}
          zIndex={0}
        />
        {/* Top-right quadrant */}
        <Sprite
          ref={backgroundSpriteTopRightRef}
          name="background_top_right"
          texture={textures.topRight}
          anchor={[0, 1]}
          x={0}
          y={0}
          zIndex={0}
        />
        {/* Bottom-left quadrant */}
        <Sprite
          ref={backgroundSpriteBottomLeftRef}
          name="background_bottom_left"
          texture={textures.bottomLeft}
          anchor={[1, 0]}
          x={0}
          y={0}
          zIndex={0}
        />
        {/* Bottom-right quadrant */}
        <Sprite
          ref={backgroundSpriteBottomRightRef}
          name="background_bottom_right"
          texture={textures.bottomRight}
          anchor={[0, 0]}
          x={0}
          y={0}
          zIndex={0}
        />
      </Container>
      {/* Full-screen invisible sprite for click detection - catches clicks on empty areas and background */}
      {/* Disabled during Hold and Win - nothing actionable on background click, and eventMode="static"
          on a full-screen sprite causes PixiJS to hit-test the entire canvas on every pointer event,
          which at 50+ clicks/sec blocks the RAF handler and causes animation jank */}
      <Sprite
        texture={PIXI.Texture.EMPTY}
        x={0}
        y={0}
        width={spriteDimensions.width}
        height={spriteDimensions.height}
        zIndex={0}
        eventMode={holdAndWinActive ? "none" : "static"}
        cursor="pointer"
        pointertap={handleBackgroundClick}
      />
    </Container>
  );
});

BackgroundAnimation.displayName = "BackgroundAnimation";

export default BackgroundAnimation;
